import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Alert, Button, Empty, Input, Select, Skeleton, Tag } from 'antd'
import { LeftOutlined, MessageOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons'
import { SUPPORT_BODY_MAX, SUPPORT_REPLY_MAX, SUPPORT_SUBJECT_MAX } from '@/api/advisorySupport'
import { useAssistant } from '../AssistantProvider'
import {
  SUPPORT_AREAS,
  TICKET_STATUS_EXPLAINER,
  TICKET_STATUS_LABEL,
} from '../mockData'
import type { SupportTicket, TicketStatusValue } from '../types'

dayjs.extend(relativeTime)

/**
 * Raise a ticket, then talk to support on it.
 *
 * The conversation lives here; the email a reply triggers only links back to
 * it. `awaiting` is also flagged in the list, on the tab and in the subtitle,
 * for anyone already in the app.
 */

const STATUS_COLOUR: Record<TicketStatusValue, string> = {
  open: 'orange',
  reviewing: 'purple',
  awaiting: 'blue',
  closed: 'default',
  unknown: 'default',
}

const YOUR_TURN = 'Support replied — your turn'

/** Relative for anything recent, absolute once "14 days ago" stops helping. */
function when(iso: string): string {
  if (!iso) return ''
  const at = dayjs(iso)
  if (!at.isValid()) return ''
  return at.isAfter(dayjs().subtract(7, 'day')) ? at.fromNow() : at.format('D MMM YYYY')
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-gray-400">{children}</div>
  )
}

function TicketForm() {
  const { state, actions } = useAssistant()
  const { supportDraft } = state

  return (
    <div className="space-y-3">
      <Button size="small" icon={<LeftOutlined />} onClick={() => actions.setSupportView('list')}>
        Support
      </Button>

      <SectionLabel>Raise a ticket</SectionLabel>

      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-600">Subject</span>
        <Input
          value={supportDraft.subject}
          onChange={(event) => actions.patchSupportDraft({ subject: event.target.value })}
          placeholder="One line on what is wrong"
          maxLength={SUPPORT_SUBJECT_MAX}
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-600">Area</span>
        <Select
          value={supportDraft.area}
          onChange={(area) => actions.patchSupportDraft({ area })}
          options={SUPPORT_AREAS.map((area) => ({ value: area, label: area }))}
          className="w-full"
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-600">
          Organisation
        </span>
        <OrganisationPicker />
      </label>

      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-600">
          What is happening
        </span>
        {/* Mirrors the server's cap; a 400 would cost the whole submission. */}
        <Input.TextArea
          value={supportDraft.body}
          onChange={(event) => actions.patchSupportDraft({ body: event.target.value })}
          placeholder="What were you trying to do? What happened instead? Any error message?"
          maxLength={SUPPORT_BODY_MAX}
          showCount={supportDraft.body.length > SUPPORT_BODY_MAX * 0.8}
          autoSize={{ minRows: 4, maxRows: 8 }}
        />
      </label>

      <div className="flex gap-2">
        <Button
          type="primary"
          className="feature-button-solid"
          loading={state.ticketSending}
          onClick={actions.submitTicket}
        >
          Send to support
        </Button>
        <Button onClick={() => actions.setSupportView('list')}>Cancel</Button>
      </div>

      <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
        <MessageOutlined className="mr-1.5" />
        Support will reply here, and we&rsquo;ll email you when they do. Only what you type here is sent to
        support.
      </p>
    </div>
  )
}

/**
 * Prefilled with the organisation on screen; the list screens have none, so the
 * user picks one there. None / General is for a ticket that is about no single
 * organisation, and submits with no platformId at all.
 */
function OrganisationPicker() {
  const { state, actions } = useAssistant()

  if (state.ticketPlatformsFailed) {
    return (
      <div className="text-[11.5px] text-gray-500">
        Could not load your organisations. Close this and try again in a moment.
      </div>
    )
  }

  return (
    <>
      <Select
        // `?? ''` so the picker shows what will actually be sent. An untouched
        // picker with no organisation on screen submits a general ticket, so it
        // reads None / General rather than a placeholder implying a choice is owed.
        value={state.ticketPlatformId ?? ''}
        onChange={(platformId: string) => actions.patchSupportDraft({ platformId })}
        options={[
          { value: '', label: 'None / General' },
          ...state.ticketPlatforms.map((option) => ({ value: option.platformId, label: option.label })),
        ]}
        loading={state.ticketPlatformsLoading}
        showSearch
        optionFilterProp="label"
        className="w-full"
      />
      {!state.ticketPlatformId && !state.ticketPlatformsLoading && (
        <span className="mt-1 block text-[10.5px] text-gray-400">
          Leave this as None / General if the ticket is not about one organisation.
        </span>
      )}
    </>
  )
}

function Message({
  author,
  at,
  body,
  fromSupport,
  sentByEmail = false,
}: {
  author: string
  at: string
  body: string
  fromSupport: boolean
  sentByEmail?: boolean
}) {
  return (
    <li
      className={`rounded-xl border p-3 ${
        fromSupport ? 'border-[var(--feature-primary)] bg-white' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="mb-1 text-[10.5px] text-gray-400">
        <span className="font-semibold text-gray-700">{author}</span>
        {when(at) ? ` · ${when(at)}` : ''}
        {sentByEmail && <span className="ml-1">· also sent to you by email</span>}
      </div>
      <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-gray-700">{body}</p>
    </li>
  )
}

function Thread({ ticket }: { ticket: SupportTicket }) {
  const { state } = useAssistant()

  return (
    <ol className="space-y-2">
      <Message author="You" at={ticket.raisedAt} body={ticket.body} fromSupport={false} />
      {state.threadLoading ? (
        <li>
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        </li>
      ) : state.threadFailed ? (
        <li>
          <Alert
            type="warning"
            showIcon
            message="Could not load the replies"
            description="Nothing is lost. Try again in a moment."
          />
        </li>
      ) : state.thread.length === 0 ? (
        <li className="text-[11.5px] text-gray-400">No reply from support yet.</li>
      ) : (
        state.thread.map((reply) => (
          <Message
            key={reply.id}
            author={reply.fromSupport ? reply.sentByName || 'Support' : 'You'}
            at={reply.sentAt}
            body={reply.body}
            fromSupport={reply.fromSupport}
            sentByEmail={reply.sentByEmail}
          />
        ))
      )}
    </ol>
  )
}

function AnswerBox({ ticket }: { ticket: SupportTicket }) {
  const { state, actions } = useAssistant()
  const answer = state.ticketAnswers[ticket.id] ?? ''

  if (ticket.status === 'closed') {
    return (
      <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[12px] leading-relaxed text-gray-600">
        This ticket is closed. Raise a new ticket if you need more help.
      </p>
    )
  }

  const yourTurn = ticket.status === 'awaiting'

  return (
    <div
      className={`space-y-2 rounded-xl border bg-white p-3 ${
        yourTurn ? 'border-[var(--feature-primary)]' : 'border-gray-200'
      }`}
    >
      {yourTurn && <div className="text-[12px] font-semibold text-gray-800">{YOUR_TURN}.</div>}
      <Input.TextArea
        value={answer}
        onChange={(event) => actions.setTicketAnswer(ticket.id, event.target.value)}
        placeholder={yourTurn ? 'Answer support here' : 'Add something for support'}
        maxLength={SUPPORT_REPLY_MAX}
        showCount={answer.length > SUPPORT_REPLY_MAX * 0.8}
        autoSize={{ minRows: 3, maxRows: 8 }}
        disabled={state.ticketAnswerSendingId === ticket.id}
        aria-label="Your answer to support"
      />
      <Button
        type="primary"
        size="small"
        icon={<SendOutlined />}
        className="feature-button-solid"
        loading={state.ticketAnswerSendingId === ticket.id}
        disabled={!answer.trim()}
        onClick={() => actions.sendTicketAnswer(ticket.id)}
      >
        Send
      </Button>
    </div>
  )
}

function TicketDetail() {
  const { state, actions } = useAssistant()
  const ticket = state.tickets.find((t) => t.id === state.activeTicketId)

  if (!ticket) {
    return (
      <Button size="small" icon={<LeftOutlined />} onClick={() => actions.setSupportView('list')}>
        Your tickets
      </Button>
    )
  }

  const raised = when(ticket.raisedAt)
  const updated = when(ticket.updatedAt)

  return (
    <div className="space-y-3">
      <Button size="small" icon={<LeftOutlined />} onClick={() => actions.setSupportView('list')}>
        Your tickets
      </Button>

      <div className="flex items-start gap-2">
        <div className="flex-1">
          <div className="text-[13.5px] font-bold leading-snug text-gray-900">{ticket.subject}</div>
          <div className="mt-1 text-[10.5px] text-gray-400">
            {ticket.reference} · {ticket.area}
            {raised ? ` · raised ${raised}` : ''}
          </div>
        </div>
        <Tag color={STATUS_COLOUR[ticket.status]}>{TICKET_STATUS_LABEL[ticket.status]}</Tag>
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[12.5px] leading-relaxed text-gray-600">
        <b>{TICKET_STATUS_LABEL[ticket.status]}</b> — {TICKET_STATUS_EXPLAINER[ticket.status]}
        {updated && <div className="mt-1 text-[11.5px] text-gray-400">Last changed {updated}</div>}
      </div>

      <SectionLabel>Conversation</SectionLabel>
      <Thread ticket={ticket} />
      <AnswerBox ticket={ticket} />
    </div>
  )
}

export function SupportTab() {
  const { state, actions } = useAssistant()

  if (state.supportView === 'form') return <TicketForm />
  if (state.supportView === 'ticket') return <TicketDetail />

  return (
    <div className="space-y-3">
      <Button
        type="primary"
        icon={<PlusOutlined />}
        className="feature-button-solid"
        onClick={() => actions.raiseTicket()}
      >
        Raise a new ticket
      </Button>

      <SectionLabel>Your tickets</SectionLabel>

      {state.ticketsLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : state.ticketsFailed ? (
        // Distinct from "no tickets" — an empty list here would read as
        // "support has nothing from you", to someone waiting on an answer.
        <Alert
          type="warning"
          showIcon
          message="Could not load your tickets"
          description="Anything you have raised is still there. Try again in a moment."
        />
      ) : state.tickets.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing raised yet." />
      ) : (
        state.tickets.map((ticket) => {
          const yourTurn = ticket.status === 'awaiting'
          return (
            <button
              key={ticket.id}
              type="button"
              onClick={() => actions.openTicketDetail(ticket.id)}
              className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border bg-white p-3 text-left transition-colors hover:border-[var(--feature-primary)] ${
                yourTurn ? 'border-[var(--feature-primary)]' : 'border-gray-200'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold leading-snug text-gray-900">{ticket.subject}</div>
                {yourTurn && (
                  <div className="mt-0.5 text-[11px] font-semibold text-[var(--feature-primary-strong)]">
                    {YOUR_TURN}
                  </div>
                )}
                <div className="mt-1 text-[10.5px] text-gray-400">
                  {ticket.reference}
                  {when(ticket.updatedAt) ? ` · ${when(ticket.updatedAt)}` : ''}
                </div>
              </div>
              <Tag color={STATUS_COLOUR[ticket.status]} className="m-0">
                {TICKET_STATUS_LABEL[ticket.status]}
              </Tag>
            </button>
          )
        })
      )}

      <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
        <MessageOutlined className="mr-1.5" />
        Support replies here, on the ticket, and we&rsquo;ll email you when they do.
      </p>
    </div>
  )
}
